'use client'

import { SidebarAccordion } from '@/components/ui/Accordion'
import { SidebarFilterChip } from '@/components/ui/Chip'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Box, GridLegacy as Grid, Typography } from '@mui/material'

import { capitalize } from 'lodash'
import type React from 'react'
import { useTranslation } from 'react-i18next'

import {
  type MenuFilterCategory,
  type SelectedMenuFilterCategory,
} from '../../domain/menu-category-filter'

type CatergoryFilterProps = {
  selectedCategories: SelectedMenuFilterCategory[]
  setSelectedCategories: (categories: MenuFilterCategory[]) => void
  filterCategories: MenuFilterCategory[] | null
}

export const getTruncatedLabel = (name: string, maxLength: number = 18) => {
  if (name.length <= maxLength) return name
  return `${name.slice(0, maxLength).trim()}...`
}

export const CatergoryFilter: React.FC<CatergoryFilterProps> = ({
  selectedCategories,
  setSelectedCategories,
  filterCategories,
}) => {
  const { t } = useTranslation()
  const ensureMenuFilterCategory = (
    category: SelectedMenuFilterCategory | MenuFilterCategory,
  ): MenuFilterCategory => ({
    ...category,
    count: 'count' in category ? category.count : 0,
    filteredCount: 'filteredCount' in category ? category.filteredCount : 0,
  })

  const mapWithLatestCounts = (
    categories: Array<SelectedMenuFilterCategory | MenuFilterCategory>,
  ): MenuFilterCategory[] =>
    categories.map((selected) => {
      const normalised = ensureMenuFilterCategory(selected)
      return {
        ...normalised,
        count: filterCategories?.find((fc) => fc.id === normalised.id)?.count ?? normalised.count,
      }
    })

  const handleChipClick = (category: MenuFilterCategory) => {
    const isSelected = selectedCategories.some(({ id }) => id === category.id)

    const updatedSelection = isSelected
      ? selectedCategories.filter(({ id }) => id !== category.id)
      : [...selectedCategories, ensureMenuFilterCategory(category)]

    const categoriesWithCounts = mapWithLatestCounts(updatedSelection)

    void setSelectedCategories(categoriesWithCounts.length > 0 ? categoriesWithCounts : [])
  }

  return (
    <SidebarAccordion
      label={capitalize(t('marketplace:menu'))}
      expandIcon={<ExpandMoreIcon />}
      defaultExpanded
      summaryProps={{
        'aria-controls': 'menu-categories-content',
        id: 'menu-categories-header',
      }}>
      <Grid container spacing={1}>
        {filterCategories && filterCategories.length > 0 ? (
          filterCategories.map((category) => {
            const isSelected = selectedCategories.some((selected) => selected.id === category.id)
            return (
              <Grid item key={category.id} md={12}>
                <SidebarFilterChip
                  selected={isSelected}
                  label={
                    <Box display="flex" alignItems="center" justifyContent={'flex-start'} gap={1}>
                      <Typography variant="body3" fontWeight={500} align="left">
                        {getTruncatedLabel(category.name)} ({category.filteredCount})
                      </Typography>
                    </Box>
                  }
                  onClick={() => handleChipClick(category)}
                />
              </Grid>
            )
          })
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No categories available
          </Typography>
        )}
      </Grid>
    </SidebarAccordion>
  )
}
